import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import footballLogo from 'assets/logos/football/main.svg';
import mlbLogo from 'assets/logos/mlb/mlb.svg';
import nbaLogo from 'assets/logos/nba/nba.svg';
import Button from 'atoms/buttons/Button';
import { SorareLogo } from 'atoms/icons/SorareLogo';
import { Horizontal, SBHorizontal } from 'atoms/layout/flex';
import { LinkOther } from 'atoms/navigation/Box';
import SmallerStarBall from 'atoms/navigation/SmallerStarBall';
import { useHeroAnimationTimings } from 'components/landing/LandingMultiSport/utils';
import ResponsiveSearchBar from 'components/search/ResponsiveSearchBar';
import { LANDING } from 'constants/__generated__/routes';
import {
  FOOTBALL_PATH,
  MLB_PATH,
  NBA_PATH,
  RIVALS_LANDING,
} from 'constants/routes';
import { useConnectionContext } from 'contexts/connection';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import useScreenSize from 'hooks/device/useScreenSize';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { glossary, navLabels, sportsLabelsMessages } from 'lib/glossary';
import AppBarProvider from 'routing/MultiSportAppBar/context/Provider';
import { laptopAndAbove, tabletAndAbove } from 'style/mediaQuery';

import rivalsLogo from './assets/rivalsYellowIcon.svg';

export const Container = styled.div`
  z-index: 2;
  width: 100vw;
  isolation: isolate;
  height: var(--navbar-height-mobile);
`;

const DarkBackground = styled.div`
  z-index: 1;
  height: 100%;
  position: relative;
  background-color: transparent;

  transition: background-color 0.75s ease-in-out;

  &.bgBlack {
    background-color: black;
  }
`;

const Wrapper = styled(SBHorizontal).attrs({ gap: 0 })`
  z-index: 1;
  position: relative;
  margin: auto;
  height: 100%;
  max-width: min(calc(var(--unit) * 165), 100%);
  padding: 0 var(--double-unit);

  background-color: transparent;
  transition: background-color 0.75s ease-in-out;
`;

const SportsLinks = styled.div`
  display: flex;
  gap: var(--triple-unit);
`;

const ResponsiveSorareLogo = styled(SorareLogo)`
  display: none;
  @media ${tabletAndAbove} {
    display: block;
  }
`;

const Logo = styled(Link)`
  display: flex;
  gap: var(--unit);
  align-items: center;
`;

const ButtonWrapper = styled(animated.div)`
  display: flex;
  gap: var(--unit);
`;

const SportButton = styled(Link)`
  display: flex;
  font-weight: bold;
  gap: var(--unit);
  align-items: center;
  padding: var(--unit) var(--unit) var(--unit) var(--double-unit);
  cursor: pointer;
  border-radius: var(--unit);
  background-color: transparent;

  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: var(--c-static-neutral-800);
  }

  & > img {
    width: var(--logo-width);
  }

  @media ${laptopAndAbove} {
    padding: var(--half-unit) var(--unit) var(--half-unit) var(--double-unit);

    & > img {
      width: 0;
      opacity: 0;
      transition:
        width 0.25s ease-in-out,
        opacity 0.25s ease-in-out;
    }

    &:hover {
      & > img {
        opacity: 1;
        width: var(--logo-width);
        margin-right: var(--unit);
      }
    }
  }
`;

const AnimatedButton = styled(Button)`
  transition: all 0.75s ease-in-out;
  /* Need to define a default value for the animation to work */
  border: 1px solid transparent;
`;

const MobileSportsBanner = styled.div`
  bottom: 0;
  width: 100vw;
  display: flex;
  position: absolute;
  transform: translateY(0%);
  justify-content: space-evenly;
  grid-template-columns: repeat(3, 1fr);
  will-change: transform;

  transition: all 0.5s ease-in-out;
  opacity: 0;
  background-color: var(--current-background-color);

  @media ${tabletAndAbove} {
    justify-content: center;
    gap: calc(var(--unit) * 7);
  }

  &.translate {
    opacity: 1;
    transform: translateY(100%);
  }

  &.blackBackground {
    background-color: black;
  }
`;

const SportsButtons = () => {
  const {
    flags: { useRivalsLanding = false },
  } = useFeatureFlags();
  const { formatMessage } = useIntlContext();
  return (
    <>
      {useRivalsLanding && (
        <LinkOther
          as={SportButton}
          to={RIVALS_LANDING}
          style={{ '--logo-width': '24px' } as React.CSSProperties}
        >
          {formatMessage(navLabels.rivals)}
          <img src={rivalsLogo} alt="" />
        </LinkOther>
      )}
      <LinkOther
        as={SportButton}
        to={FOOTBALL_PATH}
        style={{ '--logo-width': '20px' } as React.CSSProperties}
      >
        {formatMessage(sportsLabelsMessages.FOOTBALL)}
        <img
          src={footballLogo}
          alt={formatMessage(sportsLabelsMessages.FOOTBALL)}
        />
      </LinkOther>
      <LinkOther
        as={SportButton}
        to={NBA_PATH}
        style={{ '--logo-width': '10px' } as React.CSSProperties}
      >
        {formatMessage(sportsLabelsMessages.NBA)}
        <img src={nbaLogo} alt={formatMessage(sportsLabelsMessages.NBA)} />
      </LinkOther>
      <LinkOther
        as={SportButton}
        to={MLB_PATH}
        style={{ '--logo-width': '30px' } as React.CSSProperties}
      >
        {formatMessage(sportsLabelsMessages.BASEBALL)}
        <img src={mlbLogo} alt={formatMessage(sportsLabelsMessages.BASEBALL)} />
      </LinkOther>
    </>
  );
};

export const LandingAppHeader = () => {
  const { currentUser } = useCurrentUserContext();
  const { signIn, signUp } = useConnectionContext();
  const { formatMessage } = useIntlContext();
  const { firstBatch, secondBatch } = useHeroAnimationTimings();
  const { up: isDesktop } = useScreenSize('laptop');
  const [isVisible, setIsVisible] = useState(true);
  const prevScrollPos = useRef(0); // use useRef instead of useState

  const logoFadeIn = useSpring({
    opacity: secondBatch ? 1 : 0,
    config: { duration: 750 },
  });

  const buttonsSlide = useSpring({
    x: secondBatch ? 0 : -100,
    config: { duration: 500 },
  });

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const visible = prevScrollPos.current > currentScrollPos;

      if (visible !== isVisible) setIsVisible(visible);
      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isVisible]);

  return (
    <Container>
      <DarkBackground className={classNames({ bgBlack: secondBatch })}>
        <Wrapper>
          <SportsLinks>
            <Logo to={LANDING} title="Sorare">
              <SmallerStarBall />
              <animated.div style={logoFadeIn}>
                <ResponsiveSorareLogo />
              </animated.div>
            </Logo>
            {isDesktop && (
              <ButtonWrapper style={buttonsSlide}>
                <SportsButtons />
              </ButtonWrapper>
            )}
          </SportsLinks>
          <Horizontal>
            <AppBarProvider>
              <ResponsiveSearchBar />
            </AppBarProvider>
            <AnimatedButton
              size="small"
              color={secondBatch ? 'primary' : 'transparent'}
              onClick={signUp}
            >
              {formatMessage(glossary.signup)}
            </AnimatedButton>
            <AnimatedButton
              size="small"
              color={secondBatch ? 'tertiary' : 'transparent'}
              stroke={secondBatch}
              onClick={signIn}
            >
              {formatMessage(glossary.signin)}
            </AnimatedButton>
          </Horizontal>
        </Wrapper>
      </DarkBackground>
      {!isDesktop && !currentUser && (
        <MobileSportsBanner
          className={classNames({
            translate: firstBatch && isVisible,
            blackBackground: secondBatch,
          })}
        >
          <SportsButtons />
        </MobileSportsBanner>
      )}
    </Container>
  );
};
